import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ThemeToggle from '../common/ThemeToggle';
import NotificationsDropdown from '../common/NotificationsDropdown';
import Button from '../common/Button';
import AppLogo from '../common/AppLogo';
import { PAYMENT_PROVIDER } from '../../config/brand';
import { prefetchRoute } from '../../utils/routePrefetch';

const navLinks = [
  { to: '/explore', label: 'Explorer', prefetchKey: 'explore' },
  { to: '/garantie', label: 'Confiance', prefetchKey: 'guarantee' },
  { to: '/about', label: 'À propos', prefetchKey: 'about' },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenus = () => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  };

  const handleLogout = async () => {
    closeMenus();
    await logout();
    navigate('/');
  };

  const isActive = (to) => location.pathname === to;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'border-b app-border bg-[rgba(245,245,239,0.92)] backdrop-blur-xl dark:bg-[rgba(13,23,17,0.92)]'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="shrink-0"
          onClick={closeMenus}
          onMouseEnter={() => prefetchRoute('home')}
          onFocus={() => prefetchRoute('home')}
        >
          <AppLogo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                isActive(link.to)
                  ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                  : 'text-[var(--ui-text-muted)] hover:bg-[var(--ui-surface)] hover:text-[var(--ui-text)]'
              }`}
              onClick={closeMenus}
              onMouseEnter={() => prefetchRoute(link.prefetchKey)}
              onFocus={() => prefetchRoute(link.prefetchKey)}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="flex items-center gap-2 rounded-full border border-primary-100 bg-white/90 px-3 py-2 text-xs font-semibold text-primary-800 shadow-sm dark:border-primary-900/40 dark:bg-primary-950/60 dark:text-primary-100">
            <ShieldCheck className="h-4 w-4" />
            <span>{PAYMENT_PROVIDER}</span>
            <span className="text-[var(--ui-text-muted)]">KYC progressif</span>
          </div>
          <ThemeToggle />
          {user ? (
            <>
              <NotificationsDropdown />
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsProfileOpen((value) => !value)}
                  className="flex items-center gap-3 rounded-full border border-[color:var(--ui-border)] bg-[var(--ui-surface)] px-3 py-2 shadow-sm"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-700 text-sm font-bold text-white">
                    {user.first_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-[var(--ui-text)]">{user.first_name}</p>
                    <p className="text-xs text-[var(--ui-text-muted)]">
                      {user.is_admin ? 'Administration' : 'Espace membre'}
                    </p>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 text-[var(--ui-text-muted)] transition-transform ${
                      isProfileOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isProfileOpen && (
                  <>
                    <div className="fixed inset-0" onClick={() => setIsProfileOpen(false)} />
                    <div className="absolute right-0 top-[calc(100%+0.75rem)] z-10 w-64 rounded-3xl border app-border bg-[var(--ui-surface)] p-2 shadow-2xl shadow-primary-950/10">
                      <div className="rounded-2xl bg-[var(--ui-surface-alt)] p-4">
                        <p className="font-semibold text-[var(--ui-text)]">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-[var(--ui-text-muted)]">{user.email}</p>
                      </div>
                      <div className="mt-2 space-y-1">
                        <Link
                          to="/dashboard"
                          onClick={closeMenus}
                          className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-[var(--ui-text)] hover:bg-[var(--ui-surface-alt)]"
                        >
                          <LayoutDashboard className="h-4 w-4 text-primary-700" />
                          Dashboard
                        </Link>
                        {user.is_admin && (
                          <Link
                            to="/admin"
                            onClick={closeMenus}
                            className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-[var(--ui-text)] hover:bg-[var(--ui-surface-alt)]"
                          >
                            <BadgeCheck className="h-4 w-4 text-primary-700" />
                            Administration
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-[var(--ui-text)] hover:bg-red-50 hover:text-red-700"
                        >
                          <LogOut className="h-4 w-4" />
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={closeMenus}
                onMouseEnter={() => prefetchRoute('login')}
                onFocus={() => prefetchRoute('login')}
              >
                <Button variant="ghost">Connexion</Button>
              </Link>
              <Link
                to="/register"
                onClick={closeMenus}
                onMouseEnter={() => prefetchRoute('register')}
                onFocus={() => prefetchRoute('register')}
              >
                <Button>S’inscrire</Button>
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[color:var(--ui-border)] bg-[var(--ui-surface)] lg:hidden"
          onClick={() => setIsMenuOpen((value) => !value)}
          aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="border-t app-border bg-[var(--ui-surface)] lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between rounded-2xl bg-[var(--ui-surface-alt)] px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-[var(--ui-text)]">{PAYMENT_PROVIDER}</p>
                <p className="text-xs text-[var(--ui-text-muted)]">Paiement local et vérification progressive</p>
              </div>
              <ThemeToggle />
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-2xl px-4 py-3 text-sm font-medium ${
                  isActive(link.to)
                    ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                    : 'text-[var(--ui-text)] hover:bg-[var(--ui-surface-alt)]'
                }`}
                onClick={closeMenus}
              >
                {link.label}
              </Link>
            ))}

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMenus}
                  className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--ui-text)] hover:bg-[var(--ui-surface-alt)]"
                >
                  Dashboard
                </Link>
                {user.is_admin && (
                  <Link
                    to="/admin"
                    onClick={closeMenus}
                    className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--ui-text)] hover:bg-[var(--ui-surface-alt)]"
                  >
                    Administration
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-2xl px-4 py-3 text-left text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <div className="grid grid-cols-1 gap-2 pt-2">
                <Link to="/login" onClick={closeMenus}>
                  <Button variant="outline" className="w-full justify-center">
                    Connexion
                  </Button>
                </Link>
                <Link to="/register" onClick={closeMenus}>
                  <Button className="w-full justify-center">Créer un compte</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
